
import { mapActions } from 'vuex'
import * as orderTypeEnum from '~/enums/order-type'
import QuantitySelector from '~/components/create-order/quantity-selector.vue'
import Confirmation from '~/components/create-order/order-confirmation.vue'
import { createAsyncState, defineAsyncAction } from '~/utils/async-action'
import * as endpoints from '~/api/endpoints'
import OrderSummary from '~/components/create-order/order-summary.vue'
import { types as productTypes } from '~/utils/product-item-type'

export default {
  components: {
    QuantitySelector,
    OrderSummary,
    Confirmation,
  },
  props: {
    openOrders: {
      type: Array,
      default: () => [],
    },
    maxQuantity: {
      type: Number,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    itemId: {
      type: Number,
      required: true,
    },
    itemType: {
      type: String,
      required: true,
      validator: (val) => Object.values(productTypes).includes(val),
    },
    itemIsAnchored: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      quantity: 1,
      showDetails: true,
      buySaveState: createAsyncState(),
      showConfirmation: false,
      transactionFee: 0,
      orderTypeEnum,
    }
  },
  computed: {
    orderDetails() {
      const result = []
      let remainingQuantity = this.quantity

      this.openOrders.forEach((order) => {
        if (remainingQuantity > 0) {
          const quantityToTake = Math.min(order.quantity, remainingQuantity)

          result.push({
            price: order.price,
            quantity: quantityToTake,
            subTotal: order.price * quantityToTake,
          })

          remainingQuantity -= quantityToTake
        }
      })

      return result
    },

    orderSubTotal() {
      return this.orderDetails.reduce(
        (total, order) => total + order.subTotal,
        0,
      )
    },

    confirmationText() {
      const productTypeText = this.$isBottle({ itemType: this.itemType })
        ? this.$ngettext('bottle', 'bottles', this.quantity)
        : this.$ngettext('bottle from cask', 'bottles from cask', this.quantity)

      return this.$gettext(
        `Are you sure you want to add <strong>%(count)d</strong> %(productTypeText)s of %(name)s to your portfolio for <strong>%(price)s</strong>? Expected transaction fee: <strong>%(transactionFee)s</strong>.`,
        {
          count: this.$formatAsNumber(this.quantity),
          name: this.name,
          price: this.$formatAsCurrency(this.orderSubTotal),
          transactionFee: this.$formatAsCurrency(this.transactionFee),
          productTypeText,
        },
      )
    },
  },
  methods: {
    ...mapActions('trader', ['loadKeyFigures']),

    updateTransactionFee(newTransactionFee) {
      this.transactionFee = newTransactionFee
    },

    openConfirmation() {
      this.showConfirmation = true
    },

    closeConfirmation() {
      this.showConfirmation = false

      this.resetBuySaveState()
    },

    submitForm() {
      this.openConfirmation()
    },

    resetBuySaveState() {
      this.buySaveState = createAsyncState()
    },

    closeForm() {
      this.quantity = 1

      this.$emit('close-form')
    },

    toggleDetails() {
      this.showDetails = !this.showDetails
    },

    changeQuantity(quantity) {
      this.quantity = quantity
    },

    addToPortfolio: defineAsyncAction('buySaveState', async function () {
      const requestData = {
        itemId: this.itemId,
        quantity: this.quantity,
      }

      await this.$axios.$post(
        endpoints.postExchangeOrderBookBuyFillOrKill,
        requestData,
      )

      // Update trader key figures
      this.loadKeyFigures()

      this.$triggerToast({
        message: this.$gettext('Order has been placed'),
      })

      await this.$router.push({
        path: this.$nuxt.context.app.localePath('order-confirmation'),
        query: { type: 'direct-buy' },
      })

      this.$dialogs.close()
    }),
  },
}
